import { WHITE_POINTS, type Illuminant, type Lab, type LCh, type XYZ } from './types.js';

/** CIE standard constants, in the exact-rational form recommended by CIE 15:2004. */
const EPSILON = 216 / 24389;
const KAPPA = 24389 / 27;

function forward(t: number): number {
  return t > EPSILON ? Math.cbrt(t) : (KAPPA * t + 16) / 116;
}

function inverse(t: number): number {
  const t3 = t * t * t;
  return t3 > EPSILON ? t3 : (116 * t - 16) / KAPPA;
}

/**
 * XYZ -> Lab.
 *
 * `illuminant` names the white point the XYZ value is already relative to; no
 * adaptation happens here. Use {@link adaptXyz} first if you need to change it.
 */
export function xyzToLab(c: XYZ, illuminant: Illuminant = 'D65'): Lab {
  const wp = WHITE_POINTS[illuminant];
  const fx = forward(c.x / wp.x);
  const fy = forward(c.y / wp.y);
  const fz = forward(c.z / wp.z);
  return {
    L: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

export function labToXyz(c: Lab, illuminant: Illuminant = 'D65'): XYZ {
  const wp = WHITE_POINTS[illuminant];
  const fy = (c.L + 16) / 116;
  const fx = fy + c.a / 500;
  const fz = fy - c.b / 200;
  return {
    x: inverse(fx) * wp.x,
    y: inverse(fy) * wp.y,
    z: inverse(fz) * wp.z,
  };
}

export function labToLch(c: Lab): LCh {
  const C = Math.hypot(c.a, c.b);
  let h = (Math.atan2(c.b, c.a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { L: c.L, C, h: C < 1e-8 ? 0 : h };
}

export function lchToLab(c: LCh): Lab {
  const rad = (c.h * Math.PI) / 180;
  return { L: c.L, a: c.C * Math.cos(rad), b: c.C * Math.sin(rad) };
}
